import cx from 'classnames'
import React from 'react'

import { Facebook, Instagram, Twitter } from '@cinch/oxd-components/icons/src'
import Link from '@cinch/oxd-components/link/src'
import SVG from '@cinch/oxd-components/svg/src'
import Text, { TextStyles } from '@cinch/oxd-components/text/src'
import GlobalContext from '@cinch/oxd-utils/context/src'
import tokens from '@cinch/oxd-utils/tokens/src'

import * as styles from './footer.module.css'

export interface IconLink {
  title: string
  href: string
  icon: React.ReactNode
}

export interface FooterProps {
  children?: React.ReactNode
  iconLinks?: IconLink[]
  textLinks?: string[]
  isRow?: boolean
  additionalStyle?: string
}

interface FooterLinkProps {
  to: string
}

interface LinkList {
  [key: string]: {
    title: string
    href: string
  }
}

const SOCIAL_ICON_SIZE = tokens.customProperties.spacing.l

const links = [
  { title: 'Facebook', href: 'https://facebook.com/cinchuk', icon: Facebook },
  { title: 'Twitter', href: 'https://twitter.com/cinchuk', icon: Twitter },
  { title: 'Instagram', href: 'https://instagram.com/cinchuk', icon: Instagram },
]

const displayLinks = ['terms', 'privacy', 'about', 'partner']

const FooterLink: React.FC<FooterLinkProps> = ({ to, children }) => (
  <Link to={to} className={styles.link}>
    <Text type={TextStyles.caption}>{children}</Text>
  </Link>
)

export const FooterElem: React.FC<FooterProps & { i18n: Cinch.WebAppTranslations['footer'] }> = ({
  iconLinks = links,
  textLinks = displayLinks,
  isRow = false,
  additionalStyle = '',
  i18n = { terms: '', privacy: '', about: '', partner: '' },
}) => {
  const { terms, privacy, about, partner } = i18n
  const linkList: LinkList = {
    terms: { title: terms, href: '/terms-and-conditions' },
    privacy: { title: privacy, href: '/privacy' },
    about: { title: about, href: '/about-us' },
    partner: { title: partner, href: '/partners' },
  }

  return (
    <>
      <div ub-in-page="5d35904e68016f3d984f6db5" />
      <footer className={cx(styles.footer, { [styles.row]: isRow, [styles[additionalStyle]]: additionalStyle })}>
        <div data-testid="icons" className={styles.nav}>
          {iconLinks &&
            !!iconLinks.length &&
            iconLinks.map(({ title, href, icon }) => (
              <a target="_blank" rel="noopener noreferrer" key={href} href={href} title={title} className={styles.link}>
                <SVG size={SOCIAL_ICON_SIZE} children={icon} />
              </a>
            ))}
        </div>
        <div data-testid="links" className={styles.nav}>
          {textLinks.map((linkName) => (
            <FooterLink key={linkName} to={linkList[linkName].href}>
              {linkList[linkName].title}
            </FooterLink>
          ))}
        </div>
      </footer>
    </>
  )
}

const Footer: React.FC<FooterProps> = ({
  iconLinks = links,
  textLinks = displayLinks,
  isRow = false,
  additionalStyle = '',
}) => {
  const { Consumer } = GlobalContext

  return (
    <Consumer>
      {({ footer }: Cinch.Context) => (
        <FooterElem
          iconLinks={iconLinks}
          textLinks={textLinks}
          isRow={isRow}
          additionalStyle={additionalStyle}
          i18n={footer}
        />
      )}
    </Consumer>
  )
}

Footer.defaultProps = {
  isRow: false,
  iconLinks: links,
  textLinks: displayLinks,
}

export default Footer
